import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { operations as userOperations } from '../../state/modules/users';
import { selectors as userSelectors } from '../../state/modules/users';
import { operations as groupOperations } from '../../state/modules/groups';
import { selectors as groupSelectors } from '../../state/modules/groups';
import { operations as personnelTransferOperations } from '../../state/modules/personnel-transfers';
import { selectors as personnelTransferSelectors } from '../../state/modules/personnel-transfers';
import {
  operations as contractTypeOperations,
  selectors as contractTypeSelectors
} from '../../state/modules/contract-types';
import {
  operations as dutyTemplatePatternOperations,
  selectors as dutyTemplatePatternSelectors
} from '../../state/modules/standby-duty-template-patterns';
import { getCurrentUser } from '../../state/modules/current-user/operations';
import { selectCurrentUser } from '../../state/modules/current-user/selectors';
import Loader from '../general/Loader';
import DefaultFormGroup from '../../utils/forms/DefaultFormGroup';
import DefaultSingleSelector from '../../utils/forms/DefaultSingleSelector';
import PeriodSelector from '../../utils/forms/PeriodSelector';
import BaseValidationErrorsMessage from '../../utils/forms/BaseValidationErrorsMessage';
import MoreInfo from '../../utils/MoreInfo';
import { Form } from '../ui-kit';
import { intersection, sortBy, uniq } from 'lodash';

const PersonnelTransferForm = ({
  userId,
  users,
  groups,
  fetchUsers,
  fetchGroups,
  fetchCurrentUser,
  fetchContractTypes,
  contractTypes,
  newPersonnelTransfer,
  storePersonnelTransfer,
  fetchPersonnelTransfer,
  registerSaveCallback,
  registerSaveAsDraftCallback,
  personnelTransfers,
  updatePersonnelTransferFields,
  groupsInitialLoadComplete,
  groupIds,
  schedulableGroupIds,
  personnelTransferId,
  dutyTemplatePatterns,
  fetchDutyTemplatePatterns
}) => {
  const [id, setId] = useState(null);

  // Returns a promise that returns "true" if success, and "false" if failure.
  // the PersonnelTransferModal listens to this
  const save = () =>
    new Promise(resolve => {
      storePersonnelTransfer(id).then(action => {
        if (action.success) {
          // when saving a new personnel transfer, the ID will change from a
          // temporary ID to the ID in the backend
          setId(action.payload.data.id);
          resolve({ success: true, id: action.payload.data.id });
        } else {
          resolve({ success: false });
        }
      });
    });

  const saveAsDraft = () => {
    updatePersonnelTransferFields(id, { draft: true });
    return save();
  };

  const saveAsNonDraft = () => {
    updatePersonnelTransferFields(id, { draft: false });
    return save();
  };

  useEffect(() => {
    fetchUsers(userId);
    fetchGroups({ params: { group_ids: groupIds, in_hierarchy: true, presentation_type: 'basic' } });
    fetchCurrentUser();
    fetchContractTypes({ params: { group_ids: groupIds, in_hierarchy: true } });
    fetchDutyTemplatePatterns({ params: { group_ids: groupIds, in_hierarchy: true } });
  }, [fetchUsers, fetchGroups, fetchCurrentUser, fetchContractTypes, userId, groupIds]);

  useEffect(() => {
    if (personnelTransferId) {
      fetchPersonnelTransfer(personnelTransferId).then(() => {
        setId(personnelTransferId);
      });
    } else {
      newPersonnelTransfer({ user_id: userId }).then(transfer => {
        setId(transfer.id);
      });
    }
  }, [newPersonnelTransfer, fetchPersonnelTransfer, userId, personnelTransferId]);

  useEffect(() => {
    // save button is created in modal footer, which is not part of this form.
    // Register a callback when the button is pressed
    registerSaveCallback && registerSaveCallback(saveAsNonDraft);
    registerSaveAsDraftCallback && registerSaveAsDraftCallback(saveAsDraft);
  }, [id]);

  const user = useMemo(() => users[userId], [users, userId]);

  const userGroups = useMemo(() => {
    if (!user?.memberships) return [];

    return user.memberships.map(m => groups[m.group_id]).filter(Boolean);
  }, [user, groups]);

  const schedulableGroups = useMemo(
    () => schedulableGroupIds.map(id => groups[id]).filter(Boolean),
    [groups, schedulableGroupIds]
  );

  // Users can be moved from active membership groups over which the current
  // user has admin rights
  const getOriginGroupCandidates = () =>
    intersection(
      userGroups.filter(g => g.type === 'station' || g.type === 'team'),
      schedulableGroups
    );

  // Destination group candidates are all the stations and teams the current
  // user has admin rights over
  const getDestinationGroupCandidates = () =>
    uniq(schedulableGroups.filter(g => g.type === 'station' || g.type === 'team'));

  const transfer = useMemo(() => personnelTransfers[id], [personnelTransfers, id]);

  const updateFields = values => {
    // store field update in Redux directly
    updatePersonnelTransferFields(id, values);
  };

  const groupTree = groups => {
    const stations = sortBy(
      groups.filter(g => g.type == 'station'),
      ['name']
    );
    const teams = groups.filter(g => g.type == 'team');
    let list = [];

    for (const g of stations) {
      list.push(g);
      const groupTeams = teams
        .filter(t => t.parent_group_id === g.id)
        .map(t => ({ ...t, name: `${g.name} – ${t.name}` }));
      list = list.concat(sortBy(groupTeams, 'name'));
    }

    return list;
  };

  if (!user || !transfer || !groupsInitialLoadComplete) return <Loader />;
  const validationErrors = transfer.validationErrors || {};

  const timeErrors = [...(validationErrors.start_time || []), ...(validationErrors.end_time || [])];

  const i18nAttrs = I18n.t('activerecord.attributes.schedule/exchanges/personnel_transfer');

  return (
    <Form>
      <BaseValidationErrorsMessage validationErrors={validationErrors} />
      <MoreInfo
        label={I18n.t('schedules.exchanges.personnel_transfer.info_label')}
        content={I18n.t('schedules.exchanges.personnel_transfer.description')}
      />
      <DefaultFormGroup label={I18n.t('activerecord.models.user')}>
        <Form.Control plaintext readOnly defaultValue={`${user.first_name} ${user.last_name}`} />
      </DefaultFormGroup>

      <DefaultFormGroup
        label={I18n.t('activerecord.attributes.schedule/exchanges/personnel_transfer.origin_group')}
        errors={validationErrors.origin_group_id}
      >
        <DefaultSingleSelector
          objects={groupTree(getOriginGroupCandidates())}
          selectedIds={[transfer.origin_group_id]}
          handleChange={ids => updateFields({ origin_group_id: ids[0] })}
          styleFunction={i => i.type === 'station' && { fontWeight: 'bold' }}
        />
      </DefaultFormGroup>
      <DefaultFormGroup
        label={I18n.t('activerecord.attributes.schedule/exchanges/personnel_transfer.destination_group')}
        errors={validationErrors.destination_group_id}
      >
        <DefaultSingleSelector
          objects={groupTree(getDestinationGroupCandidates())}
          selectedIds={[transfer.destination_group_id]}
          handleChange={ids => updateFields({ destination_group_id: ids[0] })}
          styleFunction={i => i.type === 'station' && { fontWeight: 'bold' }}
        />
      </DefaultFormGroup>
      <DefaultFormGroup label={I18n.t('period')} errors={timeErrors}>
        <PeriodSelector
          startTime={transfer.start_time}
          endTime={transfer.end_time}
          onChange={updateFields}
          allowOpenEnded={true}
        />
      </DefaultFormGroup>
      <DefaultFormGroup label={I18n.t('comments')} htmlFor="personnel-transfer-comments">
        <Form.Control
          value={transfer.comments || ''}
          id="personnel-transfer-comments"
          as="textarea"
          rows={8}
          onChange={e => updateFields({ comments: e.target.value })}
        />
      </DefaultFormGroup>
      {contractTypes.length > 0 && (
        <DefaultFormGroup
          label={I18n.t('activerecord.models.reporting/contracts/contract_type')}
          errors={validationErrors.contract_type_id}
          hint={I18n.t('activerecord.attributes.schedule/exchanges/personnel_transfer.contract_type_hint')}
        >
          <DefaultSingleSelector
            isClearable={true}
            objects={contractTypes}
            selectedIds={[transfer.contract_type_id]}
            handleChange={ids => updateFields({ contract_type_id: ids[0] || null })}
          />
        </DefaultFormGroup>
      )}
      {dutyTemplatePatterns.length > 0 && (
        <DefaultFormGroup
          label={I18n.t('activerecord.models.schedule/standby_duty_template_pattern')}
          errors={validationErrors.override_standby_duty_template_pattern_id}
          hint={I18n.t(
            'activerecord.attributes.schedule/exchanges/personnel_transfer.standby_duty_template_pattern_hint'
          )}
        >
          <DefaultSingleSelector
            isClearable={true}
            objects={dutyTemplatePatterns}
            selectedIds={[transfer.override_standby_duty_template_pattern_id]}
            handleChange={ids => updateFields({ override_standby_duty_template_pattern_id: ids[0] || null })}
          />
        </DefaultFormGroup>
      )}
      <DefaultFormGroup>
        <Form.Check
          checked={transfer.copy_availability_codes_for_existing_intervals_only}
          id="copy_availability_codes_for_existing_intervals_only"
          name="copy_availability_codes_for_existing_intervals_only"
          onChange={e => updateFields({ copy_availability_codes_for_existing_intervals_only: e.target.checked })}
          label={i18nAttrs.copy_availability_codes_for_existing_intervals_only}
        />
      </DefaultFormGroup>
    </Form>
  );
};

const mapStateToProps = state => ({
  users: userSelectors.byId(state),
  groups: groupSelectors.byId(state),
  groupsInitialLoadComplete: groupSelectors.initialLoadComplete(state),
  currentUser: selectCurrentUser(state),
  personnelTransfers: personnelTransferSelectors.byId(state),
  contractTypes: contractTypeSelectors.values(state),
  dutyTemplatePatterns: dutyTemplatePatternSelectors.values(state)
});

const mapDispatchToProps = {
  fetchUsers: userOperations.fetch,
  fetchGroups: groupOperations.fetch,
  fetchCurrentUser: getCurrentUser,
  fetchContractTypes: contractTypeOperations.fetch,
  fetchDutyTemplatePatterns: dutyTemplatePatternOperations.fetch,
  newPersonnelTransfer: personnelTransferOperations.createNew,
  updatePersonnelTransferFields: personnelTransferOperations.updateFields,
  storePersonnelTransfer: personnelTransferOperations.storeInBackend,
  fetchPersonnelTransfer: personnelTransferOperations.fetch
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonnelTransferForm);
